/**
 * Race goal parsing and live pacing math for time-targeted distance runs
 * (10K, half marathon, marathon, etc.).
 */

export interface RaceGoal {
  distanceKm: number;
  durationSeconds: number;
  paceSecondsPerKm: number;
  /** Short label for speech, e.g. "10K", "Half marathon". */
  label: string;
}

export interface RacePacingState {
  targetDistanceKm: number;
  targetTimeSeconds: number;
  targetPaceSecondsPerKm: number;
  projectedFinishSeconds: number;
  /** Negative = ahead of schedule, positive = behind. */
  scheduleDeltaSeconds: number;
  /** Pace needed for remaining distance to hit target time. */
  requiredPaceSecondsPerKm: number;
  remainingDistanceKm: number;
  progressRatio: number;
}

const RACE_DISTANCE_KM: Readonly<Record<string, number>> = {
  '5k': 5,
  '10k': 10,
  'half marathon': 21.0975,
  'half-marathon': 21.0975,
  'half': 21.0975,
  'hm': 21.0975,
  marathon: 42.195,
  'full marathon': 42.195,
  full: 42.195,
};

const parseNumber = (value: string): number => Number(value.replace(',', '.'));

function paceFromGoal(distanceKm: number, durationSeconds: number): number {
  if (distanceKm <= 0 || durationSeconds <= 0) return 0;
  return Math.round(durationSeconds / distanceKm);
}

function labelForDistanceKm(km: number): string {
  if (Math.abs(km - 5) < 0.01) return '5K';
  if (Math.abs(km - 10) < 0.01) return '10K';
  if (Math.abs(km - 21.0975) < 0.15) return 'Half marathon';
  if (Math.abs(km - 42.195) < 0.2) return 'Marathon';
  if (km < 1) return `${Math.round(km * 1000)} meter race`;
  return `${km.toFixed(km < 10 ? 1 : 0).replace(/\.0$/, '')} kilometer race`;
}

/** Parse duration in seconds from fragments like "40 min", "2 hours", "1:45:00". */
function parseTargetDurationSeconds(text: string): number | undefined {
  const lower = text.toLowerCase();

  const hms = lower.match(/\b(\d{1,2}):(\d{2}):(\d{2})\b/);
  if (hms) {
    return parseInt(hms[1], 10) * 3600 + parseInt(hms[2], 10) * 60 + parseInt(hms[3], 10);
  }

  const hm = lower.match(/\b(\d{1,2}):(\d{2})\b/);
  if (hm) {
    const a = parseInt(hm[1], 10);
    const b = parseInt(hm[2], 10);
    // Marathon context: 1:45 or 4:00 → hours:minutes
    if (/(marathon|half|\bhm\b)/.test(lower) || a <= 6) {
      return a * 3600 + b * 60;
    }
    return a * 60 + b;
  }

  const hours = lower.match(/(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|hr|\bh\b)/);
  if (hours) {
    return Math.round(parseNumber(hours[1]) * 3600);
  }

  const mins = lower.match(/(\d+(?:\.\d+)?)\s*(?:minutes?|mins?|min\b)/);
  if (mins) {
    return Math.round(parseNumber(mins[1]) * 60);
  }

  const bareMin = lower.match(
    /(?:in|under|within|for|at|sub|below|target(?:ing)?)\s*(\d+(?:\.\d+)?)\s*(?:min(?:utes?)?|mins?\b|m\b)/,
  );
  if (bareMin) {
    return Math.round(parseNumber(bareMin[1]) * 60);
  }

  return undefined;
}

function parseTargetDistanceKm(text: string): number | undefined {
  const lower = text.toLowerCase();

  for (const [name, km] of Object.entries(RACE_DISTANCE_KM)) {
    const re = new RegExp(`\\b${name.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`, 'i');
    if (re.test(lower)) return km;
  }

  // Prefer decimals so "1.5 km" is never misread as "5 km".
  const decimalKm = lower.match(/(\d+[.,]\d+)\s*(?:k(?:m)?|kilometers?)\b/);
  if (decimalKm) return parseNumber(decimalKm[1]);

  const kmMatch = lower.match(/(^|[^\d.,])(\d+)\s*(?:k(?:m)?|kilometers?)\b/);
  if (kmMatch) return parseNumber(kmMatch[2]);

  const kOnly = lower.match(/(^|[^\d.,])(\d+)\s*k\b/);
  if (kOnly) return parseNumber(kOnly[2]);

  return undefined;
}

/** True when text describes target pace per km (not a timed race finish). */
export function isPaceTargetPlan(text: string): boolean {
  const lower = String(text || '').toLowerCase();
  return (
    /\b\d+(?:[.,]\d+)?\s*min(?:ute)?s?\s*pace\b/.test(lower) ||
    /\b\d{1,2}:\d{2}\s*(?:\/\s*km|per\s*km|pace)\b/.test(lower) ||
    /\bpace\s*(?:of\s*)?\d{1,2}:\d{2}\b/.test(lower) ||
    /\bat\s+\d+(?:[.,]\d+)?\s*min(?:ute)?s?\s*(?:\/|per)\s*k(?:m|ilometer)?\b/.test(lower)
  );
}

const RACE_PACE_REFERENCE_RE =
  /\b(?:near\s+(?:\d+\s*k(?:m)?|10k|5k|half(?:\s*marathon)?|marathon)\s+race|(?:\d+\s*k(?:m)?|10k|5k|half(?:\s*marathon)?|marathon)\s+race\s+(?:effort|pace|speed|intensity)|at\s+(?:\d+\s*k(?:m)?|10k|5k|half(?:\s*marathon)?|marathon)\s+(?:race\s+)?pace|(?:10k|5k|half(?:\s*marathon)?|marathon)\s+pace|race\s+(?:effort|pace|speed|intensity))\b/i;

/** True when a race distance is cited as effort/pace context, not a finish-line goal. */
export function isRacePaceReferenceOnly(text: string): boolean {
  return RACE_PACE_REFERENCE_RE.test(String(text || '').toLowerCase());
}

/** Sub-2:30/km race pace is not realistic — usually a duration block at race pace was misparsed. */
function isUnrealisticRaceFinishPace(distanceKm: number, durationSeconds: number): boolean {
  if (distanceKm <= 0 || durationSeconds <= 0) return true;
  return durationSeconds / distanceKm < 150;
}

/**
 * Extract a distance + time race goal from free-form coach plan text.
 * Handles 10K/ half marathon / marathon shorthand and natural language.
 * Skips "1km 5min pace" style plans — those are pace targets, not race finishes.
 */
/** Interval / rep plans are not single timed races (e.g. "3x3km with 2 min recovery"). */
export function isIntervalStylePlan(text: string): boolean {
  const lower = String(text || '').toLowerCase();
  return (
    /\d+\s*[x×]\s*\d/.test(lower) ||
    /\d+\s+times?\s+\d/.test(lower) ||
    /\binterval/.test(lower) ||
    /\brecovery\s+(?:in\s+)?between/.test(lower) ||
    /\bwith\s+\d+\s*(?:s|sec(?:ond)?s?|min(?:ute)?s?)\s*(?:easy|slow|jog|walk|recovery|rest)/.test(lower) ||
    /\bwith\s+\d+\s*min(?:ute)?s?\s+recovery/.test(lower) ||
    /\b\d+\s*[x×]\s*\d+\s*(?:k|km|kilometer)/.test(lower) ||
    (/\bwarm.?up\b/.test(lower) && (/\bcool.?down\b/.test(lower) || /\d+\s*[x×]|\d+\s+times?/.test(lower)))
  );
}

export function parseRaceGoal(text: string): RaceGoal | null {
  const raw = String(text || '').trim();
  if (!raw) return null;
  if (isPaceTargetPlan(raw)) return null;
  if (isIntervalStylePlan(raw)) return null;
  if (isRacePaceReferenceOnly(raw)) return null;

  const normalized = raw.replace(/\s+/g, ' ');

  const patterns: RegExp[] = [
    /(?<![\d.,])(\d+(?:[.,]\d+)?)\s*(?:k(?:m)?|kilometers?)\s*(?:at|in|for|within|under|by)\s*(\d+(?:[.,]\d+)?)\s*(?:min(?:utes?)?|mins?\b)/i,
    /(?<![\d.,])(\d+(?:[.,]\d+)?)\s*k\b\s*(?:at|in|for|within|under|by)\s*(\d+(?:[.,]\d+)?)\s*(?:min(?:utes?)?|mins?\b)/i,
    /(?<![\d.,])(\d+(?:[.,]\d+)?)\s*(?:k(?:m)?|kilometers?)\s*(?:at|in|for|within|under|by)\s*(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|hr|\bh\b)/i,
    /(?<![\d.,])(\d+(?:[.,]\d+)?)\s*k\b\s*(?:at|in|for|within|under|by)\s*(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|hr|\bh\b)/i,
  ];

  for (const re of patterns) {
    const m = normalized.match(re);
    if (!m) continue;
    const distanceKm = parseNumber(m[1]);
    const unit = m[0].toLowerCase();
    const durationSeconds = /hour|hr|\bh\b/.test(unit)
      ? Math.round(parseNumber(m[2]) * 3600)
      : Math.round(parseNumber(m[2]) * 60);
    if (distanceKm <= 0 || durationSeconds <= 0) continue;
    return {
      distanceKm,
      durationSeconds,
      paceSecondsPerKm: paceFromGoal(distanceKm, durationSeconds),
      label: labelForDistanceKm(distanceKm),
    };
  }

  const distanceKm = parseTargetDistanceKm(normalized);
  const durationSeconds = parseTargetDurationSeconds(normalized);
  if (distanceKm && durationSeconds && distanceKm > 0 && durationSeconds > 0) {
    if (isUnrealisticRaceFinishPace(distanceKm, durationSeconds)) return null;
    return {
      distanceKm,
      durationSeconds,
      paceSecondsPerKm: paceFromGoal(distanceKm, durationSeconds),
      label: labelForDistanceKm(distanceKm),
    };
  }

  return null;
}

/** True when the plan text describes a single timed distance race (not intervals or pace targets). */
export function isSingleRaceGoalPlan(text: string): boolean {
  if (isPaceTargetPlan(text)) return false;
  if (isIntervalStylePlan(text)) return false;
  const goal = parseRaceGoal(text);
  if (!goal) return false;
  const lower = text.toLowerCase();
  if (/\binterval|\brep\b|\brepeat|\bsprint/.test(lower)) return false;
  return true;
}

export function computeRacePacing(
  goal: Pick<RaceGoal, 'distanceKm' | 'durationSeconds' | 'paceSecondsPerKm'>,
  distanceKm: number,
  elapsedSeconds: number,
  currentPaceSecondsPerKm = 0,
): RacePacingState | null {
  if (!goal.distanceKm || !goal.durationSeconds) return null;
  if (distanceKm < 0.02 || elapsedSeconds < 20) return null;

  const remainingDistanceKm = Math.max(0, goal.distanceKm - distanceKm);
  const progressRatio = Math.min(1, Math.max(0, distanceKm / goal.distanceKm));

  const averagePaceSecondsPerKm = elapsedSeconds / distanceKm;
  const projectedFinishSeconds = averagePaceSecondsPerKm * goal.distanceKm;
  const scheduleDeltaSeconds = Math.round(projectedFinishSeconds - goal.durationSeconds);

  const remainingTimeSeconds = goal.durationSeconds - elapsedSeconds;
  let requiredPaceSecondsPerKm = goal.paceSecondsPerKm;
  if (remainingDistanceKm > 0.05 && remainingTimeSeconds > 0) {
    requiredPaceSecondsPerKm = Math.round(remainingTimeSeconds / remainingDistanceKm);
  } else if (currentPaceSecondsPerKm > 0) {
    requiredPaceSecondsPerKm = Math.round(currentPaceSecondsPerKm);
  }

  return {
    targetDistanceKm: goal.distanceKm,
    targetTimeSeconds: goal.durationSeconds,
    targetPaceSecondsPerKm: goal.paceSecondsPerKm,
    projectedFinishSeconds: Math.round(projectedFinishSeconds),
    scheduleDeltaSeconds,
    requiredPaceSecondsPerKm,
    remainingDistanceKm,
    progressRatio,
  };
}

export function formatRaceTimeSpeech(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '';
  const sec = Math.round(totalSeconds);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0 && s === 0) {
    return m === 0
      ? `${h} hour${h !== 1 ? 's' : ''}`
      : `${h} hour${h !== 1 ? 's' : ''} and ${m} minute${m !== 1 ? 's' : ''}`;
  }
  if (h > 0) {
    return `${h} hour${h !== 1 ? 's' : ''}, ${m} minute${m !== 1 ? 's' : ''}, and ${s} second${s !== 1 ? 's' : ''}`;
  }
  if (m > 0 && s === 0) return `${m} minute${m !== 1 ? 's' : ''}`;
  if (m > 0) return `${m} minute${m !== 1 ? 's' : ''} and ${s} second${s !== 1 ? 's' : ''}`;
  return `${s} second${s !== 1 ? 's' : ''}`;
}

export function formatScheduleDeltaSpeech(deltaSeconds: number): string {
  const abs = Math.abs(Math.round(deltaSeconds));
  if (abs < 8) return 'right on schedule';
  const unit =
    abs >= 60
      ? `${Math.round(abs / 60)} minute${Math.round(abs / 60) !== 1 ? 's' : ''}`
      : `${abs} second${abs !== 1 ? 's' : ''}`;
  return deltaSeconds < 0 ? `${unit} ahead of schedule` : `${unit} behind schedule`;
}

function formatPaceSpeechShort(secPerKm: number): string {
  if (!secPerKm || !Number.isFinite(secPerKm)) return '';
  const totalSec = Math.max(0, Math.round(secPerKm));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m <= 0) return `${s} seconds`;
  if (s === 0) return `${m} minute${m !== 1 ? 's' : ''}`;
  return `${m} minute${m !== 1 ? 's' : ''} and ${s} seconds`;
}

export function buildRacePacingSpeech(
  pacing: RacePacingState,
  goalLabel: string,
  targetTimeSpeech: string,
  currentPaceSpeech: string,
  targetPaceSpeech: string,
): string {
  const schedule = formatScheduleDeltaSpeech(pacing.scheduleDeltaSeconds);
  const remainingKm = pacing.remainingDistanceKm;
  const rem =
    remainingKm >= 1
      ? `${remainingKm.toFixed(1).replace(/\.0$/, '')} kilometers left`
      : `${Math.round(remainingKm * 1000)} meters left`;

  if (Math.abs(pacing.scheduleDeltaSeconds) <= 8) {
    return `${goalLabel} on pace for ${targetTimeSpeech} — ${schedule}. ${rem}. Hold about ${targetPaceSpeech}. Current ${currentPaceSpeech}. Stay smooth and locked in.`;
  }
  if (pacing.scheduleDeltaSeconds < 0) {
    return `${goalLabel}: ${schedule} for ${targetTimeSpeech}. ${rem} — ease slightly and hold around ${targetPaceSpeech}. You're at ${currentPaceSpeech}.`;
  }
  const req = formatPaceSpeechShort(pacing.requiredPaceSecondsPerKm);
  const fasterBy = pacing.targetPaceSecondsPerKm - pacing.requiredPaceSecondsPerKm;
  const pickupHint =
    fasterBy >= 5
      ? `pick up to about ${req} per kilometer — roughly ${Math.round(fasterBy)} seconds faster than your ${targetPaceSpeech} goal pace`
      : `pick up slightly — aim for about ${req} per kilometer to hit ${targetTimeSpeech}`;
  return `${goalLabel}: ${schedule} for ${targetTimeSpeech}. ${rem} — ${pickupHint}. You're at ${currentPaceSpeech}.`;
}
